/**
 * Supabase Auth Context
 * 
 * Provides authentication state throughout the app.
 * Supports:
 * - Magic link (passwordless email)
 * - Password authentication
 * - Password reset flow
 * - Session persistence
 * - Auto-refresh
 * - User profile (name, avatar) stored in user_metadata
 * - Profile photo upload to Supabase Storage
 * - Club-based access control (Lumen8 staff can access all clubs)
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabase";

// ============================================================
// CLUB ACCESS CONTROL
// ============================================================
const LUMEN8_DOMAIN = "@lumen8.com.au";
const APP_CLUB = process.env.REACT_APP_DEFAULT_CLUB || null;

// User roles
export const USER_ROLES = {
  CLUB: 'club',        // Regular club users - full access to their club
  PRODUCER: 'producer', // Production houses (MKTG, KOJO) - uploaders only
  OPERATOR: 'operator', // Lumen8 operators - can access operator portal
  VENUE_OPS: 'venue_ops', // Venue operations (AFL/Marvel) - read-only + Venue Content category
  ADMIN: 'admin',      // Future: full admin access
};

// Production house domains - uploaders only, no MatchFlow
// Add new production partner domains here
const PRODUCER_DOMAINS = [
  "@mktg.com",
  "@kojo.com",
  "@kojo.co",  // Keep both variants just in case
  // Add more as needed:
  // "@newagency.com.au",
];

// Check if user is Lumen8 staff (can access all clubs)
const isLumen8Staff = (email) => {
  if (!email) return false;
  return email.toLowerCase().endsWith(LUMEN8_DOMAIN);
};

// Check if user is from a production house
const isProducerByDomain = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  return PRODUCER_DOMAINS.some((domain) => lower.endsWith(domain));
};

// Check if producer has access to a specific club
// Returns true if:
// - User has no allowed_clubs restriction (legacy producers, access all)
// - User's allowed_clubs includes the target club
const producerHasClubAccess = (user, clubId) => {
  if (!user) return false;
  
  const allowedClubs = user.user_metadata?.allowed_clubs;
  
  // No restriction set = access all clubs (backwards compatible)
  if (!allowedClubs || !Array.isArray(allowedClubs) || allowedClubs.length === 0) {
    return true;
  }
  
  // Check if this club is in allowed list (case-insensitive)
  const normalizedClub = (clubId || "").toLowerCase();
  return allowedClubs.some(c => (c || "").toLowerCase() === normalizedClub);
};

// Check if user has operator role (Lumen8 staff with operator access)
// Operators are Lumen8 staff who can access the operator portal
const isOperator = (user) => {
  if (!user?.email) return false;
  // All Lumen8 staff are operators by default
  // In future, this could check user.user_metadata.role === 'operator'
  return isLumen8Staff(user.email);
};

// Check if user has producer role
const isProducer = (user) => {
  if (!user?.email) return false;
  // Check explicit metadata role first (set manually via Supabase)
  if (user.user_metadata?.role === USER_ROLES.PRODUCER) return true;
  // Fall back to domain detection
  return isProducerByDomain(user.email);
};

// Check if user has venue_ops role (AFL/Marvel venue operations)
// Venue ops have read-only access to all clubs + can edit "Venue Content" category
const isVenueOps = (user) => {
  if (!user?.email) return false;
  // Check explicit metadata role (set via Supabase)
  return user.user_metadata?.role === USER_ROLES.VENUE_OPS;
};

// Get user's role
const getUserRole = (user) => {
  if (!user?.email) return null;
  
  // Check for explicit role in metadata first
  const metadataRole = user.user_metadata?.role;
  if (metadataRole && Object.values(USER_ROLES).includes(metadataRole)) {
    return metadataRole;
  }
  
  // Default: Lumen8 staff are operators, producers by domain, others are club users
  if (isLumen8Staff(user.email)) {
    return USER_ROLES.OPERATOR;
  }
  
  if (isProducerByDomain(user.email)) {
    return USER_ROLES.PRODUCER;
  }
  
  return USER_ROLES.CLUB;
};

// Check if user has access to this club's portal
const hasClubAccess = (user) => {
  if (!user?.email) return false;
  
  // Lumen8 staff can access ALL clubs
  if (isLumen8Staff(user.email)) {
    return true;
  }
  
  // Venue ops can access ALL clubs (read-only)
  if (isVenueOps(user)) {
    return true;
  }
  
  // Producers: check if they have access to this specific club
  if (isProducerByDomain(user.email) || user.user_metadata?.role === USER_ROLES.PRODUCER) {
    // If no APP_CLUB is set (dev mode), allow access
    if (!APP_CLUB) return true;
    // Check producer's allowed clubs
    return producerHasClubAccess(user, APP_CLUB);
  }
  
  // If no club is configured for this app, allow access (dev mode)
  if (!APP_CLUB) {
    return true;
  }
  
  // Check user's assigned club from metadata
  const userClub = user.user_metadata?.club;
  
  // If user has no club assigned yet, they're a new user completing signup
  // Only allow them to continue on this portal (where they'll be assigned)
  if (!userClub) {
    // Check if they need to complete their profile - if so, allow access
    // (they'll be assigned this club after completing profile)
    const hasName = user.user_metadata?.display_name || user.user_metadata?.name;
    if (!hasName) {
      return true; // New user, allow them to complete profile on this portal
    }
    // User has a name but no club - this is an old user from before club restrictions
    // Deny access and force them to sign out and sign in fresh to get assigned
    return false;
  }
  
  // Check if user's club matches this app's club
  return userClub === APP_CLUB;
};

// ============================================================
// AUTH CONTEXT
// ============================================================
const AuthContext = createContext(null);

// ============================================================
// AUTH PROVIDER
// ============================================================
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // Extract display name from user metadata
  const getUserName = (user) => {
    return user?.user_metadata?.display_name || user?.user_metadata?.name || null;
  };

  // Extract avatar URL from user metadata
  const getUserAvatar = (user) => {
    return user?.user_metadata?.avatar_url || null;
  };

  // Check if user has a password set (vs magic link only)
  const hasPasswordSet = (user) => {
    // Users who signed up with password will have has_password flag in metadata
    return user?.user_metadata?.has_password === true;
  };

  useEffect(() => {
    // If Supabase isn't configured, skip auth
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    // ============================================================
    // SSO TOKEN HANDOFF: Check for tokens from Hub redirect
    // ============================================================
    const handleSSOTokens = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access_token');
      const refreshToken = urlParams.get('refresh_token');

      if (accessToken && refreshToken) {
        console.log("[Auth] SSO token handoff detected, setting session...");
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (error) {
            console.error("[Auth] SSO session error:", error.message);
          } else {
            console.log("[Auth] SSO session established successfully");
            setSession(data.session);
            setUser(data.session?.user ?? null);
          }
        } catch (err) {
          console.error("[Auth] SSO token handling failed:", err);
        }

        // Clean tokens from URL (security)
        urlParams.delete('access_token');
        urlParams.delete('refresh_token');
        const cleanUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        
        setLoading(false);
        return true; // Signal that we handled SSO
      }
      return false; // No SSO tokens found
    };

    // Try SSO first, then fall back to normal session check
    handleSSOTokens().then((handledSSO) => {
      if (handledSSO) return; // Already set session from SSO

      // Get initial session (normal flow)
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth event:", event);
        
        // Detect password recovery flow
        if (event === "PASSWORD_RECOVERY") {
          setIsPasswordRecovery(true);
          // Don't set user/session yet - wait for password to be reset
          setLoading(false);
          return;
        }
        
        // If user completes password reset, clear the recovery flag
        if (event === "USER_UPDATED" && isPasswordRecovery) {
          setIsPasswordRecovery(false);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [isPasswordRecovery]);

  // Sign in with magic link
  const signInWithMagicLink = async (email) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Redirect back to the app after clicking the link
        emailRedirectTo: window.location.origin,
      },
    });

    if (error) throw error;
    return { success: true };
  };

  // Sign in with password
  const signInWithPassword = async (email, password) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  };

  // Sign up with password
  const signUp = async (email, password) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          has_password: true,
        },
      },
    });

    if (error) throw error;
    return data;
  };

  // Update user's display name in metadata
  const updateUserName = async (displayName) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    const { data, error } = await supabase.auth.updateUser({
      data: { display_name: displayName }
    });

    if (error) throw error;
    
    // Update local user state with new metadata
    setUser(data.user);
    return data;
  };

  // Update user's password (or set password for magic link users)
  // Also clears must_change_password flag if set (for temp password flow)
  const updatePassword = async (newPassword) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
      data: { 
        has_password: true,
        must_change_password: false  // Clear temp password flag
      }
    });

    if (error) throw error;
    
    setUser(data.user);
    return data;
  };

  // Upload avatar to Supabase Storage and update user metadata
  const uploadAvatar = async (file) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    if (!user) {
      throw new Error("No user logged in");
    }

    // Create unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    // Update user metadata with avatar URL
    const { data, error: updateError } = await supabase.auth.updateUser({
      data: { avatar_url: publicUrl }
    });

    if (updateError) throw updateError;

    setUser(data.user);
    return publicUrl;
  };

  // Remove avatar
  const removeAvatar = async () => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    // Update user metadata to remove avatar URL
    const { data, error } = await supabase.auth.updateUser({
      data: { avatar_url: null }
    });

    if (error) throw error;

    setUser(data.user);
    return data;
  };

  // Send password reset email
  const sendPasswordReset = async (email) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });

    if (error) throw error;
    return { success: true };
  };

  // Complete password reset (called after user clicks reset link)
  const completePasswordReset = async (newPassword) => {
    if (!isSupabaseConfigured()) {
      throw new Error("Supabase not configured");
    }

    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
      data: { has_password: true }
    });

    if (error) throw error;
    
    // Clear recovery mode
    setIsPasswordRecovery(false);
    setUser(data.user);
    
    return data;
  };

  // Clear password recovery mode (if user wants to cancel)
  const clearPasswordRecovery = () => {
    setIsPasswordRecovery(false);
  };

  // Sign out
  const signOut = async () => {
    if (!isSupabaseConfigured()) return;

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Assign club to user on first sign-up (non-Lumen8 users)
  const assignUserClub = async () => {
    if (!user || !APP_CLUB) return;
    if (isLumen8Staff(user.email)) return; // Don't assign club to Lumen8 staff
    if (user.user_metadata?.club) return; // Already has a club
    
    // Assign this app's club to the user
    const { data, error } = await supabase.auth.updateUser({
      data: { club: APP_CLUB }
    });
    
    if (!error && data.user) {
      setUser(data.user);
    }
  };

  // Check club access
  const userHasClubAccess = hasClubAccess(user);
  const userIsLumen8Staff = user ? isLumen8Staff(user.email) : false;
  const userIsOperator = user ? isOperator(user) : false;
  const userIsProducer = user ? isProducer(user) : false;
  const userIsVenueOps = user ? isVenueOps(user) : false;
  const userRole = user ? getUserRole(user) : null;

  // Check if user must change password (temp password flow)
  const mustChangePassword = !!user?.user_metadata?.must_change_password;

  const value = {
    user,
    session,
    loading,
    isAuthenticated: !!user && !isPasswordRecovery && userHasClubAccess,
    isPasswordRecovery,
    userName: getUserName(user),
    userAvatar: getUserAvatar(user),
    userEmail: user?.email || null,
    userClub: user?.user_metadata?.club || null,
    allowedClubs: user?.user_metadata?.allowed_clubs || null, // For producers with club restrictions
    allowedVenues: user?.user_metadata?.allowed_venues || null, // For venue_ops with venue restrictions
    hasPassword: hasPasswordSet(user),
    needsProfileComplete: !!user && !getUserName(user) && !isPasswordRecovery,
    mustChangePassword, // For temp password flow - forces password change screen
    // Club access control
    hasClubAccess: userHasClubAccess,
    isLumen8Staff: userIsLumen8Staff,
    appClub: APP_CLUB,
    accessDenied: !!user && !isPasswordRecovery && !userHasClubAccess,
    // Operator access
    isOperator: userIsOperator,
    // Producer access (uploaders only, no MatchFlow)
    isProducer: userIsProducer,
    // Venue ops access (read-only + Venue Content category)
    isVenueOps: userIsVenueOps,
    userRole,
    canAccessOperatorPortal: userIsOperator,
    assignUserClub,
    signInWithMagicLink,
    signInWithPassword,
    signUp,
    signOut,
    updateUserName,
    updatePassword,
    uploadAvatar,
    removeAvatar,
    sendPasswordReset,
    completePasswordReset,
    clearPasswordRecovery,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================
// HOOK
// ============================================================
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
